import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Category, categorySelected } from '../services/category/get-category/types';
import { FetchError, getActiveMenus } from '../services';
//import { COOKIE_CATEGORY_ID } from '../components/constants';
import { getCookie, setCookie } from '../utils';
import { DateTime } from 'luxon';
import { Menu } from '../services/active-menus/get-active-menus/types';

interface CategoryContextProps {
    categories: Category[];
    categoryProducts: any[];
    filteredCategories: Category[];
    filteredSubCategories: categorySelected[];
    mainCategoryId: number | null;
    handleCategorySelection: (categoryId: number) => void;
    handleSubCategorySelection: (categoryId: number, subCategory: number) => categorySelected[];
    error: string | null;
    isCategoryLoading: boolean;
}

const CategoryContext = createContext<CategoryContextProps | undefined>(undefined);

export const useCategory = (): CategoryContextProps => {
    const context = useContext(CategoryContext);
    if (!context) {
        throw new Error('useCategory must be used within a CategoryProvider');
    }
    return context;
};

const convertCategories = (activeMenu: any): { categories: Category[]; items: any[] } => {
    const categories: Category[] = [];
    const itemsByCategory: any[] = [];
    let categoryIdCounter = 1; // Start category IDs from 1

    function createCategory(name: string, parentCategory: number, description: string, image: string): Category {
        return {
            categoryId: categoryIdCounter++,
            name: name.toUpperCase(), // Convert name to uppercase
            parentCategory: parentCategory,
            displayOrder: 0,
            localizedDescription: description,
            image: image
        };
    }

    activeMenu.forEach(
        (category: {
            name: any;
            imageUrl: any;
            subLevels: {
                items: any;
                name: any;
                description: any;
                imageUrl: any;
                subLevels: { name: any; description: any; imageUrl: any }[];
            }[];
        }) => {
            // Create the main category
            const mainCategory = createCategory(category.name, 0, `${category.name}`, category.imageUrl || '');
            categories.push(mainCategory);

            // Initialize the items array for the main category
            itemsByCategory[mainCategory.categoryId] = [];

            // Process subLevels
            category.subLevels.forEach(
                (subLevel: {
                    items: any;
                    name: any;
                    description: any;
                    imageUrl: any;
                    subLevels: { name: any; description: any; imageUrl: any }[];
                }) => {
                    const subCategory = createCategory(
                        subLevel.name,
                        mainCategory.categoryId,
                        subLevel.description || '',
                        subLevel.imageUrl || ''
                    );

                    categories.push(subCategory);

                    if (subLevel.items.length > 0) {
                        itemsByCategory[subCategory.categoryId] = [];

                        const processItems = (level: { name?: any; description?: any; imageUrl?: any; items?: any; subLevels?: any }) => {
                            // Process items in the current level
                            level.items.forEach((item: { id: any; name: any; price: any; imageUrl: any; description: any }) => {
                                const mappedItem = {
                                    productId: item.id,
                                    name: item.name,
                                    price: item.price || 0,
                                    primaryImageUrl: item.imageUrl || '',
                                    recordId: item.id,
                                    productNumber: item.id,
                                    totalRatings: 0,
                                    description: item.description || '',
                                    basePrice: item.price || 0,
                                    isMasterProduct: false,
                                    masterProductId: 0,
                                    defaultUnitOfMeasure: 'EA'
                                };
                                if (
                                    !itemsByCategory[subCategory.categoryId].some((row: { productId: string }) => row.productId === item.id)
                                ) {
                                    itemsByCategory[subCategory.categoryId].push(mappedItem);
                                }
                            });

                            // Recursively process nested sublevels
                            if (level.subLevels && level.subLevels.length > 0) {
                                level.subLevels.forEach(
                                    (nestedSubLevel: { name?: any; description?: any; imageUrl?: any; items?: any; subLevels?: any }) => {
                                        processItems(nestedSubLevel);
                                    }
                                );
                            }
                        };

                        // Process the current subLevel and its nested sublevels
                        processItems(subLevel);
                    } else {
                        // Process second subLevels
                        subLevel.subLevels.forEach((subLevel: { name: any; description: any; imageUrl: any }) => {
                            const subSecondCategory = createCategory(
                                subLevel.name,
                                subCategory.categoryId,
                                subLevel.description || '',
                                subLevel.imageUrl || ''
                            );

                            categories.push(subSecondCategory);
                            itemsByCategory[subSecondCategory.categoryId] = [];

                            const processItems = (level: {
                                name?: any;
                                description?: any;
                                imageUrl?: any;
                                items?: any;
                                subLevels?: any;
                            }) => {
                                // Process items in the current level
                                level.items.forEach((item: { id: any; name: any; price: any; imageUrl: any; description: any }) => {
                                    const mappedItem = {
                                        productId: item.id,
                                        name: item.name,
                                        price: item.price || 0,
                                        primaryImageUrl: item.imageUrl || '',
                                        recordId: item.id,
                                        productNumber: item.id,
                                        totalRatings: 0,
                                        description: item.description || '',
                                        basePrice: item.price || 0,
                                        isMasterProduct: false,
                                        masterProductId: 0,
                                        defaultUnitOfMeasure: 'EA'
                                    };
                                    itemsByCategory[subSecondCategory.categoryId].push(mappedItem);
                                });

                                // Recursively process nested sublevels
                                if (level.subLevels && level.subLevels.length > 0) {
                                    level.subLevels.forEach(
                                        (nestedSubLevel: {
                                            name?: any;
                                            description?: any;
                                            imageUrl?: any;
                                            items?: any;
                                            subLevels?: any;
                                        }) => {
                                            processItems(nestedSubLevel);
                                        }
                                    );
                                }
                            };

                            // Process the current subLevel and its nested sublevels
                            processItems(subLevel);
                        });
                    }
                }
            );
        }
    );

    return { categories: categories, items: itemsByCategory };
};

const getAvailableMenus = (activeMenus: Menu[]) => {
    return activeMenus.filter(item => {
        if (!item.availabilityRanges || item.availabilityRanges.length === 0) {
            return false;
        }

        return item.availabilityRanges.some(range => {
            const currentDateTime = DateTime.now();
            const validFrom = DateTime.fromISO(range.validFrom.slice(0, -1), { zone: 'America/Los_Angeles' });
            const validTo = DateTime.fromISO(range.validTo.slice(0, -1), { zone: 'America/Los_Angeles' });
            return currentDateTime >= validFrom && currentDateTime <= validTo;
        });
    });
};

export const CategoryProvider: React.FC = ({ children }) => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [categoryProducts, setcategoryProducts] = useState<any[]>([]);
    const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
    const [filteredSubCategories, setFilteredSubCategories] = useState<categorySelected[]>([]);
    const [mainCategoryId, setMainCategoryId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isCategoryLoading, setIsLoading] = useState<boolean>(true);

    const fetchCategories = useCallback(async () => {
        try {
            setIsLoading(true);
            //let fetchedCategories = await getCategories();
            const activeMenus = await getActiveMenus();

            if (!Array.isArray(activeMenus)) {
                throw new Error('Failed to fetch active Menus.');
            }

            const availableMenus = getAvailableMenus(activeMenus);

            const convertedStructure = convertCategories(availableMenus);
            setcategoryProducts(convertedStructure.items);
            const fetchedCategories = convertedStructure.categories;

            if (!Array.isArray(fetchedCategories)) {
                throw new Error('Failed to fetch categories.');
            }

            setCategories(fetchedCategories);

            const mainCategoryId = Number(1);
            setMainCategoryId(mainCategoryId);

            const filteredChildCategories = fetchedCategories.filter(
                (category: { parentCategory: number }) => category.parentCategory === mainCategoryId
            );
            if (!filteredChildCategories.length) {
                throw new FetchError(`Parent category ID ${mainCategoryId} not a parentCategory in fetched categories`);
            }

            setFilteredCategories(filteredChildCategories);

            setError(null);
        } catch (error) {
            console.error('CategoryContext: Failed to fetch categories:', error);
            if (error instanceof FetchError) {
                if (error.status === 400) {
                    setError('Bad Request: The server could not understand the request to fetch categories');
                } else if (error.status === 404) {
                    setError('Not Found: The requested categories could not be found.');
                } else {
                    setError(error.message || 'Unknown error');
                }
            } else if (error instanceof Error) {
                setError(error.message);
            } else {
                setError('Unknown error');
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        void fetchCategories();
    }, [fetchCategories]);

    const handleCategorySelection = (categoryId: number) => {
        const filtered = categories.filter(category => category.parentCategory === categoryId);
        setFilteredCategories(filtered);
        setMainCategoryId(categoryId);
    };

    const handleSubCategorySelection = (categoryId: number, subCategory: number) => {
        let defaultSubCategoryId: number;
        const preSelectedSubCategory = getCookie('subCategory');
        const filtered = categories.filter(category => category.parentCategory === categoryId);
        if (subCategory === 0 && preSelectedSubCategory && filtered.length) {
            defaultSubCategoryId =
                filtered.length && filtered.find(category => category.categoryId === parseInt(preSelectedSubCategory, 10))
                    ? parseInt(preSelectedSubCategory, 10)
                    : filtered[0].categoryId;
            setCookie('subCategory', '');
        } else {
            defaultSubCategoryId = filtered.length && subCategory === 0 ? filtered[0].categoryId : subCategory;
            if (subCategory !== 0) {
                setCookie('subCategory', subCategory.toString());
            }
        }
        const subCategoryList = filtered.map(category => {
            return {
                categoryId: category.categoryId,
                selected: category.categoryId === defaultSubCategoryId,
                name: category.name
            };
        });
        setFilteredSubCategories(subCategoryList);
        return subCategoryList;
    };

    return (
        <CategoryContext.Provider
            value={{
                categories,
                categoryProducts,
                filteredCategories,
                filteredSubCategories,
                mainCategoryId,
                handleCategorySelection,
                handleSubCategorySelection,
                error,
                isCategoryLoading
            }}
        >
            {children}
        </CategoryContext.Provider>
    );
};
